import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { KPI, StatusBadge, SeverityBadge } from "@/components/Primitives";
import { Monitor, Ticket, ShieldAlert, RefreshCw, AlertTriangle, Download, Copy, Package, Terminal } from "lucide-react";

export default function ClientDetail() {
  const { id } = useParams();
  const [client, setClient] = useState(null);
  const [assets, setAssets] = useState([]);
  const [agents, setAgents] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [tab, setTab] = useState("overview");
  const [deploy, setDeploy] = useState(null);
  const [deployLoading, setDeployLoading] = useState(false);

  const load = async () => {
    const [c, a, ag, t] = await Promise.allSettled([
      api.get(`/clients/${id}`),
      api.get("/assets", { params: { client_id: id, limit: 1000 } }),
      api.get("/agents"),
      api.get("/tickets", { params: { client_id: id, limit: 1000 } }),
    ]);

    if (c.status === "fulfilled") setClient(c.value.data);
    if (a.status === "fulfilled") setAssets(a.value.data);
    if (ag.status === "fulfilled") setAgents(ag.value.data);
    if (t.status === "fulfilled") setTickets(t.value.data);
  };

  useEffect(() => { load(); }, [id]);

  const generateDeployment = async () => {
    setDeployLoading(true);
    try {
      const { data } = await api.post("/agents/install-token", { client_id: id });
      setDeploy(data);
    } finally {
      setDeployLoading(false);
    }
  };

  const assetIds = useMemo(() => new Set(assets.map(a => a.id)), [assets]);

  const clientAgents = useMemo(() => {
    return agents.filter(a => a.client_id === id || assetIds.has(a.asset_id));
  }, [agents, id, assetIds]);

  const openTickets = tickets.filter(t => t.status !== "closed");
  const criticalTickets = openTickets.filter(t => t.priority === "critical");

  const offlineAgents = clientAgents.filter(a => a.status !== "online");
  const nonCompliant = assets.filter(a =>
    a.compliance_status === "non_compliant" ||
    a.status === "at_risk" ||
    a.security_status === "at_risk"
  );

  const needsUpdates = assets.filter(a =>
    a.patch_status === "missing_updates" ||
    a.needs_updates ||
    a.os_patch_age_days > 30
  );

  const securityAlerts = assets.filter(a =>
    a.security_status === "critical" ||
    a.security_status === "at_risk" ||
    a.vulnerability_count > 0
  );

  if (!client) return <div className="text-zinc-500 text-sm">Loading client…</div>;

  return (
    <div className="space-y-6">
      <div>
        <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-mono">Client Operations</div>
        <h1 className="font-display text-3xl font-semibold tracking-tight mt-1">{client.name}</h1>
        <p className="text-sm text-zinc-500 mt-1">{client.industry} · {client.region} · {client.tier}</p>
      </div>

      <div className="grid grid-cols-2 xl:grid-cols-8 gap-4">
        <DrillCard icon={Monitor} label="Endpoints" value={assets.length || client.asset_count || 0} onClick={() => setTab("endpoints")} />
        <DrillCard icon={Monitor} label="Online Agents" value={clientAgents.filter(a => a.status === "online").length} onClick={() => setTab("agents")} tone="good" />
        <DrillCard icon={AlertTriangle} label="Offline Agents" value={offlineAgents.length} onClick={() => setTab("agents")} tone="bad" />
        <DrillCard icon={ShieldAlert} label="Non-Compliant" value={nonCompliant.length || client.compliance_findings || 0} onClick={() => setTab("compliance")} tone="warn" />
        <DrillCard icon={RefreshCw} label="Needs Updates" value={needsUpdates.length} onClick={() => setTab("updates")} tone="warn" />
        <DrillCard icon={Ticket} label="Open Tickets" value={openTickets.length || client.tickets_open || 0} onClick={() => setTab("tickets")} />
        <DrillCard icon={ShieldAlert} label="Security Alerts" value={securityAlerts.length || client.security_findings || 0} onClick={() => setTab("security")} tone="bad" />
        <DrillCard icon={Package} label="Deployment" value="Package" onClick={() => setTab("deployment")} />
      </div>

      <div className="flex flex-wrap gap-2 border-b border-zinc-900">
        {[
          ["overview", "Overview"],
          ["agents", "Agents"],
          ["endpoints", "Endpoints"],
          ["tickets", "Tickets"],
          ["compliance", "Compliance"],
          ["updates", "Needs Updates"],
          ["security", "Security"],
          ["sites", "Sites"],
          ["deployment", "Deployment"],
        ].map(([k, label]) => (
          <button
            key={k}
            onClick={() => setTab(k)}
            className={`px-3 py-2 text-sm border-b-2 ${tab === k ? "border-blue-500 text-white" : "border-transparent text-zinc-500 hover:text-zinc-200"}`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === "overview" && (
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
          <Panel title="Operational Snapshot">
            <div className="grid grid-cols-2 gap-3">
              <KPI label="Health" value={`${client.health_score || 0}%`} />
              <KPI label="Sites" value={client.site_count || client.sites?.length || 0} />
              <KPI label="Critical Tickets" value={criticalTickets.length} tone={criticalTickets.length ? "bad" : "default"} />
              <KPI label="Offline Agents" value={offlineAgents.length} tone={offlineAgents.length ? "bad" : "good"} />
            </div>
          </Panel>

          <Panel title="Top Risks">
            <Risk label="Offline endpoints" value={offlineAgents.length} />
            <Risk label="Non-compliant assets" value={nonCompliant.length} />
            <Risk label="Devices needing updates" value={needsUpdates.length} />
            <Risk label="Security alerts" value={securityAlerts.length} />
          </Panel>

          <Panel title="Fast Drilldowns">
            <button onClick={() => setTab("agents")} className="quick">View all client agents</button>
            <button onClick={() => setTab("tickets")} className="quick">View open tickets</button>
            <button onClick={() => setTab("deployment")} className="quick">Generate deployment package</button>
            <button onClick={() => setTab("updates")} className="quick">View devices needing updates</button>
          </Panel>
        </div>
      )}

      {tab === "agents" && (
        <Panel title={`Agents (${clientAgents.length})`}>
          <AgentTable agents={clientAgents} />
        </Panel>
      )}

      {tab === "endpoints" && (
        <Panel title={`Endpoints (${assets.length})`}>
          <AssetTable assets={assets} />
        </Panel>
      )}

      {tab === "tickets" && (
        <Panel title={`Open Tickets (${openTickets.length})`}>
          <TicketTable tickets={openTickets} />
        </Panel>
      )}

      {tab === "compliance" && (
        <Panel title={`Non-Compliant Items (${nonCompliant.length})`}>
          <AssetTable assets={nonCompliant} />
        </Panel>
      )}

      {tab === "updates" && (
        <Panel title={`Devices Needing Updates (${needsUpdates.length})`}>
          <AssetTable assets={needsUpdates} />
        </Panel>
      )}

      {tab === "security" && (
        <Panel title={`Security Alerts (${securityAlerts.length})`}>
          <AssetTable assets={securityAlerts} />
        </Panel>
      )}

      {tab === "deployment" && (
        <DeploymentPanel
          client={client}
          deploy={deploy}
          loading={deployLoading}
          onGenerate={generateDeployment}
        />
      )}

      {tab === "sites" && (
        <Panel title={`Sites (${client.sites?.length || 0})`}>
          <table className="w-full text-sm">
            <thead className="bg-zinc-900/50 text-[10px] uppercase tracking-widest text-zinc-500 font-mono">
              <tr><th className="text-left px-4 py-3">Name</th><th className="text-left px-4 py-3">Address</th><th className="text-left px-4 py-3">Timezone</th></tr>
            </thead>
            <tbody>
              {(client.sites || []).map(s => (
                <tr key={s.id || s.name} className="border-b border-zinc-900">
                  <td className="px-4 py-2">{s.name}</td>
                  <td className="px-4 py-2 text-zinc-400">{s.address}</td>
                  <td className="px-4 py-2 font-mono text-xs text-zinc-500">{s.timezone}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Panel>
      )}
    </div>
  );
}

function DeploymentPanel({ client, deploy, loading, onGenerate }) {
  const copy = async (text) => {
    await navigator.clipboard.writeText(text || "");
  };

  return (
    <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
      <Panel title="Deployment Package">
        <div className="p-5 space-y-4">
          <div>
            <div className="font-display text-xl font-semibold">Client-Aware Agent Deployment</div>
            <p className="text-sm text-zinc-500 mt-2">
              Generate a deployment token for {client.name}. Any PC installed with this package will auto-assign to this client.
            </p>
          </div>

          <button
            onClick={onGenerate}
            disabled={loading}
            className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-zinc-800 disabled:text-zinc-500 rounded px-4 py-3 text-sm text-white flex items-center justify-center gap-2"
          >
            <Package size={15} />
            {loading ? "Generating..." : "Generate Deployment Package"}
          </button>

          {deploy && (
            <div className="text-xs text-zinc-500 font-mono">
              Client ID: {deploy.client_id || "tenant-wide"}<br />
              Expires: {deploy.expires_days || 30} days
            </div>
          )}
        </div>
      </Panel>

      <Panel title="Single PC Install">
        <div className="p-5 space-y-3">
          <a href="/downloads/APOCALYNXAgent.exe" className="w-full bg-zinc-900 border border-zinc-800 hover:border-blue-700 rounded px-4 py-3 text-sm text-zinc-200 flex items-center justify-center gap-2">
            <Download size={15} /> Download Agent EXE
          </a>

          <a href="/downloads/install-apocalynx-agent.ps1" className="w-full bg-zinc-900 border border-zinc-800 hover:border-blue-700 rounded px-4 py-3 text-sm text-zinc-200 flex items-center justify-center gap-2">
            <Download size={15} /> Download Installer Script
          </a>

          {deploy ? (
            <>
              <CodeBlock text={deploy.powershell_command} />
              <button onClick={() => copy(deploy.powershell_command)} className="copybtn">
                <Copy size={14} /> Copy PowerShell Install Command
              </button>
            </>
          ) : (
            <div className="text-sm text-zinc-500">Generate a deployment package first.</div>
          )}
        </div>
      </Panel>

      <Panel title="Mass Deployment">
        <div className="p-5 space-y-3">
          <div className="text-sm text-zinc-400">
            Use this for GPO Startup Script, RMM push, or domain-wide deployment.
          </div>

          {deploy ? (
            <>
              <CodeBlock text={deploy.gpo_startup_script} />
              <button onClick={() => copy(deploy.gpo_startup_script)} className="copybtn">
                <Terminal size={14} /> Copy GPO Startup Script
              </button>

              <CodeBlock text={deploy.deploy_command} />
              <button onClick={() => copy(deploy.deploy_command)} className="copybtn">
                <Copy size={14} /> Copy MSI Command
              </button>
            </>
          ) : (
            <div className="text-sm text-zinc-500">Generate a deployment package first.</div>
          )}
        </div>
      </Panel>
    </div>
  );
}

function CodeBlock({ text }) {
  return (
    <pre className="bg-black border border-zinc-900 rounded p-3 text-xs font-mono text-zinc-300 whitespace-pre-wrap break-all max-h-72 overflow-auto">
      {text || "—"}
    </pre>
  );
}

function DrillCard({ icon: Icon, label, value, onClick, tone }) {
  const color = tone === "bad" ? "text-red-300" : tone === "warn" ? "text-amber-300" : tone === "good" ? "text-emerald-300" : "text-white";
  return (
    <button onClick={onClick} className="bg-zinc-950 border border-zinc-900 rounded-md p-4 text-left hover:border-blue-700 transition-colors">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">{label}</div>
        <Icon size={15} className="text-blue-400" />
      </div>
      <div className={`font-mono text-3xl font-bold mt-4 ${color}`}>{value}</div>
      <div className="text-[10px] uppercase tracking-widest text-zinc-600 font-mono mt-2">Click to drill down</div>
    </button>
  );
}

function Panel({ title, children }) {
  return (
    <div className="bg-zinc-950 border border-zinc-900 rounded-md overflow-hidden">
      <div className="px-5 py-3 border-b border-zinc-900 font-display font-medium">{title}</div>
      <div className="p-0">{children}</div>
    </div>
  );
}

function AgentTable({ agents }) {
  return (
    <table className="w-full text-sm">
      <thead className="bg-zinc-900/50 text-[10px] uppercase tracking-widest text-zinc-500 font-mono">
        <tr>
          <th className="text-left px-4 py-3">Hostname</th>
          <th className="text-left px-4 py-3">User</th>
          <th className="text-left px-4 py-3">OS</th>
          <th className="text-left px-4 py-3">IP</th>
          <th className="text-left px-4 py-3">CPU</th>
          <th className="text-left px-4 py-3">Memory</th>
          <th className="text-left px-4 py-3">Disk</th>
          <th className="text-left px-4 py-3">Last HB</th>
          <th className="text-left px-4 py-3">Status</th>
        </tr>
      </thead>
      <tbody>
        {agents.map(a => (
          <tr key={a.id} className="border-b border-zinc-900 hover:bg-zinc-900/40">
            <td className="px-4 py-2 font-mono">{a.hostname || a.id}</td>
            <td className="px-4 py-2 text-zinc-400">{a.metrics?.current_user || "—"}</td>
            <td className="px-4 py-2 text-zinc-400">{a.os}</td>
            <td className="px-4 py-2 font-mono text-xs">{a.metrics?.ip_address || "—"}</td>
            <td className="px-4 py-2 font-mono text-xs">{a.metrics?.cpu ?? "—"}%</td>
            <td className="px-4 py-2 font-mono text-xs">{a.metrics?.mem ?? "—"}%</td>
            <td className="px-4 py-2 font-mono text-xs">{a.metrics?.disk ?? "—"}%</td>
            <td className="px-4 py-2 font-mono text-xs text-zinc-500">{a.last_heartbeat?.slice(11,19) || "—"}</td>
            <td className="px-4 py-2"><StatusBadge status={a.status} /></td>
          </tr>
        ))}
        {agents.length === 0 && <tr><td colSpan={9} className="px-4 py-8 text-sm text-zinc-500">No agents linked to this client yet.</td></tr>}
      </tbody>
    </table>
  );
}

function AssetTable({ assets }) {
  return (
    <table className="w-full text-sm">
      <thead className="bg-zinc-900/50 text-[10px] uppercase tracking-widest text-zinc-500 font-mono">
        <tr>
          <th className="text-left px-4 py-3">Hostname</th>
          <th className="text-left px-4 py-3">Type</th>
          <th className="text-left px-4 py-3">OS</th>
          <th className="text-left px-4 py-3">User</th>
          <th className="text-left px-4 py-3">Status</th>
          <th className="text-left px-4 py-3">Compliance</th>
          <th className="text-left px-4 py-3">Patch</th>
        </tr>
      </thead>
      <tbody>
        {assets.map(a => (
          <tr key={a.id} className="border-b border-zinc-900 hover:bg-zinc-900/40">
            <td className="px-4 py-2"><Link to={`/assets/${a.id}`} className="font-mono hover:text-blue-400">{a.hostname || a.name || a.id}</Link></td>
            <td className="px-4 py-2 text-zinc-400">{a.type || "endpoint"}</td>
            <td className="px-4 py-2 text-zinc-400">{a.os || "—"}</td>
            <td className="px-4 py-2 text-zinc-400">{a.assigned_user || a.current_user || "—"}</td>
            <td className="px-4 py-2"><StatusBadge status={a.status || "active"} /></td>
            <td className="px-4 py-2 text-zinc-400">{a.compliance_status || "unknown"}</td>
            <td className="px-4 py-2 text-zinc-400">{a.patch_status || (a.needs_updates ? "needs_updates" : "unknown")}</td>
          </tr>
        ))}
        {assets.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-sm text-zinc-500">No records found.</td></tr>}
      </tbody>
    </table>
  );
}

function TicketTable({ tickets }) {
  return (
    <table className="w-full text-sm">
      <thead className="bg-zinc-900/50 text-[10px] uppercase tracking-widest text-zinc-500 font-mono">
        <tr>
          <th className="text-left px-4 py-3">#</th>
          <th className="text-left px-4 py-3">Title</th>
          <th className="text-left px-4 py-3">Priority</th>
          <th className="text-left px-4 py-3">Status</th>
          <th className="text-left px-4 py-3">SLA</th>
          <th className="text-left px-4 py-3">Created</th>
        </tr>
      </thead>
      <tbody>
        {tickets.map(t => (
          <tr key={t.id} className="border-b border-zinc-900 hover:bg-zinc-900/40">
            <td className="px-4 py-2 font-mono text-zinc-500">#{t.number}</td>
            <td className="px-4 py-2"><Link to={`/tickets/${t.id}`} className="hover:text-blue-400">{t.title}</Link></td>
            <td className="px-4 py-2"><SeverityBadge level={t.priority} /></td>
            <td className="px-4 py-2"><StatusBadge status={t.status} /></td>
            <td className="px-4 py-2 font-mono text-xs text-zinc-500">{t.sla_deadline?.slice(0,10)}</td>
            <td className="px-4 py-2 font-mono text-xs text-zinc-500">{t.created_at?.slice(0,10)}</td>
          </tr>
        ))}
        {tickets.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-sm text-zinc-500">No open tickets.</td></tr>}
      </tbody>
    </table>
  );
}

function Risk({ label, value }) {
  return (
    <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-900">
      <span className="text-sm text-zinc-400">{label}</span>
      <span className="font-mono text-lg">{value}</span>
    </div>
  );
}
